import { Business, Service } from "@turizi/shared";
import { getAttrValue } from "@/lib/models-utils";
import { useTranslation } from "react-i18next";
import { IconRosetteDiscountCheck, IconStar, IconUsers } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { BUSINESSES_PATH, SERVICES_PATH } from "@/lib/navigation";
import { wrapInLink } from "@/lib/draw-utils";


interface Props {
    service: Service;
    hideProviders?: boolean;
    showSoldBy?: boolean;
    hideCity?: boolean;
    links?: boolean;
}

export default function StayTitle({
    service,
    hideProviders,
    showSoldBy = false,
    hideCity = false,
    links = false
}: Props) {
    const { t } = useTranslation();

    const capacity = getAttrValue(service, 'capacity') || null;
    const singleBeds = getAttrValue(service, 'single_beds') || null;
    const doubleBeds = getAttrValue(service, 'double_beds') || null;
    const regime = getAttrValue(service, 'regime') || null;
    const special_view = getAttrValue(service, 'special_view') || null;
    const city = service.providers?.[0]?.city || null;

    const roomDescrption = [
        getAttrValue(service, 'home_type'),
        getAttrValue(service, 'room_type'),
        getAttrValue(service, 'category'),
    ].filter(Boolean).join(' ');

    const bedsDescrption = [
        singleBeds && `${singleBeds} ${Number(singleBeds) > 1 ? t('single_beds') : t('single_bed')}`,
        doubleBeds && `${doubleBeds} ${Number(doubleBeds) > 1 ? t('double_beds') : t('double_bed')}`,
    ].filter(Boolean).join(' · ');

    return (
        <div className="w-full text-gray-900">
            <div>
                {!hideCity && (
                    <div className="text-xs text-gray-400 font-normal">
                        {city}
                    </div>
                )}
                <div className="font-semibold">
                    {!hideProviders && (service?.providers || []).map((provider: Business, index: number) => (
                        <div key={provider.id + '_' + index}>
                            {links ? (
                                <Link
                                    to={`${BUSINESSES_PATH}/${provider.id}`}
                                    className="flex flex-wrap gap-x-2 gap-y-0.5 items-baseline"
                                >
                                    <div className="text-xs">
                                        {t(provider.category)} {provider.name}
                                    </div>
                                    <div className="flex items-center gap-0.5">
                                        {Array.from({ length: Number(getAttrValue(provider, 'stars')) || 0 }).map((_, i) => (
                                            <IconStar key={i} size={10} color="#f59e0b" fill="#f59e0b" stroke={1} />
                                        ))}
                                    </div>
                                </Link>
                            ) : (
                                <div className="flex flex-wrap gap-x-2 gap-y-0.5 items-baseline text-gray-400">
                                    <div className="text-sm">
                                        {t(provider.category)} {provider.name}
                                    </div>
                                    <div className="flex items-center gap-0.5">
                                        {Array.from({ length: Number(getAttrValue(provider, 'stars')) || 0 }).map((_, i) => (
                                            <IconStar key={i} size={10} color="#f59e0b" fill="#f59e0b" stroke={1} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {(!service.providers || service.providers.length === 0) && (
                <div className="flex flex-wrap gap-x-2 gap-y-0.5 items-baseline text-gray-400">
                    <div className="text-sm">
                        {t('no_providers_assigned')}
                    </div>
                </div>
            )}

            {wrapInLink(
                <div className="w-full flex flex-wrap gap-2 items-center font-medium">
                    {service.name && (
                        <div className="text-sm text-gray-900">
                            {service.name}
                        </div>
                    )}
                    <div>
                        <div className="flex flex-wrap items-center gap-1 w-full text-gray-800">
                            {roomDescrption} {bedsDescrption ? ' - ' + bedsDescrption : ''}
                        </div>
                    </div>
                    &middot;
                    {capacity && (
                        <div className="flex gap-1 items-center">
                            <IconUsers size={12} color="gray" />
                            <div className="text-xs text-gray-800">{capacity}</div>
                        </div>
                    )}

                    {/* Service Features */}
                    <div className="flex gap-1">
                        {special_view && (
                            <span className="custom-badge bg-yellow-100 text-yellow-800">
                                {special_view}
                            </span>
                        )}
                        {regime && (
                            <span className="custom-badge bg-blue-100 text-blue-800">
                                {regime}
                            </span>
                        )}
                    </div>
                    {/* <div className="text-slate-800 font-medium bg-gray-100 rounded-full px-2 py-0.5 flex items-center gap-1">
                        <IconUsers size={10} color="#9ca3af" stroke={2} /> {service.min_pax_per_booking || '0'} <span className="text-gray-400">-</span> {service.max_pax_per_booking || '∞'}
                    </div> */}
                    <div className="text-xxs text-slate-500 font-light bg-gray-100 rounded-full px-2 py-0.5">
                        #{service.id}
                    </div>
                </div>
                , links ? `${SERVICES_PATH}/${service.id}` : null)
            }

            {/* Sold By Information */}
            {showSoldBy && (
                <div className="flex items-center gap-1  text-gray-500">
                    <IconRosetteDiscountCheck size={16} className="text-green-500" />
                    <span>{t('sold_by')} <span className="font-medium">{service?.seller?.name}</span></span>
                </div>
            )}
        </div>
    )
}